import es from 'date-fns/locale/es';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { default as React, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as PROFILE from "../../../constants/profile";
import { useAuth } from '../../../hooks/useAuth';
import moment from 'moment';

registerLocale('es', es)

function IncomeReport() {

  // #region vars and consts

  const { hasPermission } = useAuth();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [report, setReport] = useState([]);
  const [summary, setSummary] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(false);  
  const [filters, setFilters] = useState({
    'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'day': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },    
    'date': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },    
    'ebsaVisaMCOrigVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ebsaAmexOrigVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ebsaCashOrigVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ebsaVisaMCVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ebsaAmexVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ebsaCashVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ecofyCashVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'inAppEcofyVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'prepaidEcofyVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'billedEcofyVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'ticketEcofyVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'cardEbsaVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'officeEbsaVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'officeEcofyVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    'cardAppVal': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  });

  // #endregion

  // #region methods

  const getIncomeReport = (
    startDate,
    endDate
  ) => {
    console.group("Getting income report");
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/airportService/v1/incomeReport/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start: startDate,
        end: endDate
      })
    }).then(response => response.json()).then(data => {      
      if (data.responseCode === 'OK') {
        console.info("Report:", data);
        const _report = data.responseObject.incomeReportOutDto;
        const _summary = data.responseObject.totalIncomeReport;        
        setReport(_report);
        setSummary(_summary)
      }
      setLoading(false);
      console.groupEnd();
    }, (error) => {
      console.error("Error getting income report", error);
      console.groupEnd();
    });
  };

  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const exportAsExcel = () => {
    import('xlsx').then(xlsx => {
      const heading = [[        
        "Día",
        "Fecha",
        "Ebsa Visa/MC",
        "Ebsa Amex",
        "Ebsa Efectivo",
        "Cash Ecofy",
        "In app Ecofy",
        "Prepaid Ecofy",
        "Billed Ecofy",
        "Boleta Ecofy",
        "Tarjetas Ebsa",
        "Caja Ebsa",
        "Caja Ecofy",
        "Tarjeta App"
      ]];
      const worksheet = xlsx.utils.json_to_sheet(report, { origin: 'A2', skipHeader: true });
      xlsx.utils.sheet_add_aoa(worksheet, heading, { origin: 'A1' });
      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileName = 'ReporteDeIngresos';
      import('file-saver').then(module => {
        if (module && module.default) {
          let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          let EXCEL_EXTENSION = '.xlsx';
          const data = new Blob([excelBuffer], {
            type: EXCEL_TYPE
          });
          module.default.saveAs(data, fileName + '_' + new Date().getTime() + EXCEL_EXTENSION);
        }
      });
    });
  };

  // #endregion

  // #region events

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilter(value);
  }

  const onSearchButtonPress = () => {
    setReport([]);
    getIncomeReport(
      startDate.valueOf(),
      endDate.valueOf()
    );
  }

  const onExportExcelButtonPress = () => {
    exportAsExcel();
  };

  useEffect(() => {
    const todayMoment = moment();
    const startDateMoment = moment(todayMoment).startOf('month').startOf('day');
    const endDateMoment = todayMoment.startOf('day');
    setStartDate(startDateMoment.toDate());
    setEndDate(endDateMoment.toDate());
    scrollToTop();
  }, []);

  // #endregion

  // #region templates

  const filtersTemplate = (
    <>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Fecha inicial
            </Form.Label>
            <DatePicker
              controlId="startDateInput"
              dateFormat="dd/MMM/yyyy"
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              locale="es"
              className="w-100 app_datepicker" />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Fecha final
            </Form.Label>
            <DatePicker
              controlId="endDateInput"
              dateFormat="dd/MMM/yyyy"
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              locale="es"
              className="w-100 app_datepicker" />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            type="button"
            icon="pi pi-search"
            onClick={onSearchButtonPress}
            className="p-button-raised p-button-sm p-button-primary"
            label='Buscar' />
        </Col>
      </Row>
    </>
  );

  const reportHeaderTemplate = (
    <>
      <Row>
        <Col>
          <span className="p-input-icon-left">
            <i className="pi pi-filter" />
            <InputText
              value={globalFilter}
              onChange={onGlobalFilterChange}
              placeholder="Filtrar"
              className='p-inputtext-sm'             
            />
          </span>
        </Col>
        <Col>
          {
            hasPermission(PROFILE.PERMISSIONS.DOWNLOAD_REPORTS) && (
              <Button
                className="p-button-raised p-button-sm p-button-success mr-2 float-end"
                type="button"
                icon="pi pi-file-excel"                
                data-pr-tooltip="XLS"
                label='Exportar'       
                onClick={onExportExcelButtonPress}                
              />
            )
          }
        </Col>
      </Row>
    </>
  );

  const ebsaVisaMCOrigValColumnTemplate = (rowData) => {
    return rowData.ebsaVisaMCOrigVal.toFixed(2);
  };
  const ebsaAmexOrigValColumnTemplate = (rowData) => {
    return rowData.ebsaAmexOrigVal.toFixed(2);
  };
  const ebsaCashOrigValColumnTemplate = (rowData) => {
    return rowData.ebsaCashOrigVal.toFixed(2);
  };
  const ebsaVisaMCValColumnTemplate = (rowData) => {
    return rowData.ebsaVisaMCVal.toFixed(2);
  };
  const ebsaAmexValColumnTemplate = (rowData) => {
    return rowData.ebsaAmexVal.toFixed(2);
  };
  const ebsaCashValColumnTemplate = (rowData) => {
    return rowData.ebsaCashVal.toFixed(2);
  };
  const ecofyCashValColumnTemplate = (rowData) => {
    return rowData.ecofyCashVal.toFixed(2);
  };
  const inAppEcofyValColumnTemplate = (rowData) => {
    return rowData.inAppEcofyVal.toFixed(2);
  };
  const prepaidEcofyValColumnTemplate = (rowData) => {
    return rowData.prepaidEcofyVal.toFixed(2);
  };
  const billedEcofyValColumnTemplate = (rowData) => {
    return rowData.billedEcofyVal.toFixed(2);
  };
  const ticketEcofyValColumnTemplate = (rowData) => {
    return rowData.ticketEcofyVal.toFixed(2);
  };
  const cardEbsaValColumnTemplate = (rowData) => {
    return rowData.cardEbsaVal.toFixed(2);
  };
  const officeEbsaValColumnTemplate = (rowData) => {
    return rowData.officeEbsaVal.toFixed(2);
  };
  const officeEcofyValColumnTemplate = (rowData) => {
    return rowData.officeEcofyVal.toFixed(2);
  };
  const cardAppValColumnTemplate = (rowData) => {
    return rowData.cardAppVal.toFixed(2);
  };

  let headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="" colSpan={2} />
        <Column header="Ventas" colSpan={3} />
        <Column header="Ventas menos comisiones" colSpan={8} />
        <Column header="Deposito" colSpan={4} />
      </Row>
      <Row>
        <Column header="Día" sortable filter filterField="day" filterPlaceholder='Buscar por Día' className='app-w-150-px text-end' />
        <Column header="Fecha" sortable filter filterField="date" filterPlaceholder='Buscar por Fecha' className='app-w-150-px text-end' />
        <Column header="Ebsa Visa/MC" sortable filter filterField="ebsaVisaMCOrigVal" filterPlaceholder='Buscar por Ebsa Visa/MC' className='app-w-150-px text-end' />
        <Column header="Ebsa Amex" sortable filter filterField="ebsaAmexOrigVal" filterPlaceholder='Buscar por Ebsa Amex' className='app-w-150-px text-end' />
        <Column header="Ebsa Efectivo" sortable filter filterField="ebsaCashOrigVal" filterPlaceholder='Buscar por Ebsa Efectivo' className='app-w-150-px text-end' />
        <Column header="Ebsa Visa/MC" sortable filter filterField="ebsaVisaMCVal" filterPlaceholder='Buscar por Ebsa Visa/MC' className='app-w-150-px text-end' />
        <Column header="Ebsa Amex" sortable filter filterField="ebsaAmexVal" filterPlaceholder='Buscar por Ebsa Amex' className='app-w-150-px text-end' />
        <Column header="Ebsa Efectivo" sortable filter filterField="ebsaCashVal" filterPlaceholder='Buscar por Ebsa Efectivo' className='app-w-150-px text-end' />
        <Column header="Cash Ecofy" sortable filter filterField="ecofyCashVal" filterPlaceholder='Buscar por Cash Ecofy' className='app-w-150-px text-end' />
        <Column header="In app Ecofy" sortable filter filterField="inAppEcofyVal" filterPlaceholder='Buscar por In app Ecofy' className='app-w-150-px text-end' />
        <Column header="Prepaid Ecofy" sortable filter filterField="prepaidEcofyVal" filterPlaceholder='Buscar por Prepaid Ecofy' className='app-w-150-px text-end' />
        <Column header="Billed Ecofy" sortable filter filterField="billedEcofyVal" filterPlaceholder='Buscar por Billed Ecofy' className='app-w-150-px text-end' />
        <Column header="Boleta Ecofy" sortable filter filterField="ticketEcofyVal" filterPlaceholder='Buscar por Boleta Ecofy' className='app-w-150-px text-end' />
        <Column header="Tarjetas Ebsa" sortable filter filterField="cardEbsaVal" filterPlaceholder='Buscar por Tarjetas Ebsa' className='app-w-150-px text-end' />
        <Column header="Caja Ebsa" sortable filter filterField="officeEbsaVal" filterPlaceholder='Buscar por Caja Ebsa' className='app-w-150-px text-end' />
        <Column header="Caja Ecofy" sortable filter filterField="officeEcofyVal" filterPlaceholder='Buscar por Caja Ecofy' className='app-w-150-px text-end' />
        <Column header="Tarjeta App" sortable filter filterField="cardAppVal" filterPlaceholder='Buscar por Tarjeta App' className='app-w-150-px text-end' />
      </Row>
    </ColumnGroup>
  );
    

  let footerGroup = (
    <ColumnGroup>
      <Row>
        <Column footer="Subtotal:" colSpan={2} footerStyle={{ textAlign: 'right' }} />        
        <Column footer={summary? summary.ebsaVisaMCOrigTotalVal.toFixed(2) : 0.00} className='text-end' />
        <Column footer={summary? summary.ebsaAmexOrigTotalVal.toFixed(2) : 0.00} className='text-end' />
        <Column footer={summary? summary.ebsaCashOrigTotalVal.toFixed(2) : 0.00} className='text-end' />        
        <Column footer={summary? summary.ebsaVisaMCTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.ebsaAmexTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.ebsaCashTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.ecofyCashTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.inAppEcofyTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.prepaidEcofyTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.billedEcofyTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.ticketEcofyTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.cardEbsaTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.officeEbsaTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.officeEcofyTotalVal.toFixed(2): 0.00} className='text-end' />
        <Column footer={summary? summary.cardAppTotalVal.toFixed(2): 0.00} className='text-end' />
      </Row>
    </ColumnGroup>
  );  

  const resultTemplate = (
    <>
      <div>
        <div className="card">
          <DataTable
            value={report}
            paginator
            className="p-datatable-customers"
            showGridlines
            stripedRows
            header={reportHeaderTemplate}
            rows={50}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rowsPerPageOptions={[10, 25, 50]}
            filters={filters}
            filterDisplay="menu"
            loading={loading}
            responsiveLayout="scroll"
            globalFilterFields={[
              'day',
              'date',
              'ebsaVisaMCOrigVal',
              'ebsaAmexOrigVal',
              'ebsaCashOrigVal',              
              'ebsaVisaMCVal',
              'ebsaAmexVal',
              'ebsaCashVal',
              'ecofyCashVal',
              'inAppEcofyVal',
              'prepaidEcofyVal',
              'billedEcofyVal',
              'ticketEcofyVal',
              'cardEbsaVal',
              'officeEbsaVal',
              'officeEcofyVal',
              'cardAppVal',
            ]}
            emptyMessage="No records found."
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            headerColumnGroup={headerGroup}
            footerColumnGroup={footerGroup}
          >            
            <Column field="day" className='app-w-150-px text-end' />
            <Column field="date" className='app-w-150-px text-end' />            
            <Column field="ebsaVisaMCOrigVal" className='app-w-150-px text-end' body={ebsaVisaMCOrigValColumnTemplate} />
            <Column field="ebsaAmexOrigVal" className='app-w-150-px text-end' body={ebsaAmexOrigValColumnTemplate} />
            <Column field="ebsaCashOrigVal" className='app-w-150-px text-end' body={ebsaCashOrigValColumnTemplate} />
            <Column field="ebsaVisaMCVal" className='app-w-150-px text-end' body={ebsaVisaMCValColumnTemplate} />
            <Column field="ebsaAmexVal" className='app-w-150-px text-end' body={ebsaAmexValColumnTemplate} />
            <Column field="ebsaCashVal" className='app-w-150-px text-end' body={ebsaCashValColumnTemplate} />
            <Column field="ecofyCashVal" className='app-w-150-px text-end' body={ecofyCashValColumnTemplate} />
            <Column field="inAppEcofyVal" className='app-w-150-px text-end' body={inAppEcofyValColumnTemplate} />
            <Column field="prepaidEcofyVal" className='app-w-150-px text-end' body={prepaidEcofyValColumnTemplate} />
            <Column field="billedEcofyVal" className='app-w-150-px text-end' body={billedEcofyValColumnTemplate} />
            <Column field="ticketEcofyVal" className='app-w-150-px text-end' body={ticketEcofyValColumnTemplate} />
            <Column field="cardEbsaVal" className='app-w-150-px text-end' body={cardEbsaValColumnTemplate} />
            <Column field="officeEbsaVal" className='app-w-150-px text-end' body={officeEbsaValColumnTemplate} />
            <Column field="officeEcofyVal" className='app-w-150-px text-end' body={officeEcofyValColumnTemplate} />
            <Column field="cardAppVal" className='app-w-150-px text-end' body={cardAppValColumnTemplate} />
          </DataTable>
        </div>
      </div>
    </>
  );

  const reportTemplate = (
    <>
      <article>
        <Row>
          <Col>
            <h1 className='app-font'>
              Reporte de Ingresos
            </h1>
          </Col>
        </Row>
        <Row>
          <Col>
            {filtersTemplate}
          </Col>
        </Row>
        <Row className='pt-2'>
          <Col>
            {resultTemplate}
          </Col>
        </Row>
      </article>
    </>
  );

  // #endregion

  return (
    <>
      {reportTemplate}
    </>
  );
}

export default IncomeReport;